import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import HolderOutlined from '@ant-design/icons/HolderOutlined';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, List, Tooltip, Typography } from 'antd/lib';
import { filter, sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { useCustomizableTableContext } from 'src/components/table/CustomizableTableContext';
import { PRIMARY_COLOR } from 'src/theme';

interface ISidebarGridProps {}

export const SidebarGrid: React.FC<ISidebarGridProps> = () => {
    const { allColumns, setColumnHidden, setColumnsOrder } = useCustomizableTableContext();

    const draggableColumns = useMemo(() => {
        return sortBy(
            filter(allColumns, col => col.key !== 'action'),
            'order'
        );
    }, [allColumns]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = draggableColumns.findIndex(item => item.key === active.id);
            const newIndex = draggableColumns.findIndex(item => item.key === over?.id);
            const reordered = arrayMove(draggableColumns, oldIndex, newIndex);

            setColumnsOrder(reordered.map(col => String(col.key)));
        }
    };

    const toggleColumnVisibility = (columnKey: string, currentVisible: boolean) => {
        setColumnHidden(columnKey, !currentVisible);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
            <SortableContext items={draggableColumns.map(item => String(item.key))} strategy={verticalListSortingStrategy}>
                <List
                    dataSource={draggableColumns}
                    renderItem={item => (
                        <SortableColumnItem
                            key={item.key}
                            column={item}
                            onToggleVisibility={() => toggleColumnVisibility(String(item.key), !item.hidden)}
                        />
                    )}
                    size="small"
                />
            </SortableContext>
        </DndContext>
    );
};

interface SortableColumnItemProps {
    column: { key: React.Key; title?: any; hidden?: boolean; hideable?: boolean };
    onToggleVisibility: () => void;
}

const SortableColumnItem = ({ column, onToggleVisibility }: SortableColumnItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(column.key) });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
            }}
        >
            <List.Item
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    border: '1px solid #f0f0f0',
                    marginBottom: '4px',
                    backgroundColor: column.hidden ? '#fafafa' : '#fff',
                    cursor: 'default',
                }}
            >
                <HolderOutlined
                    {...listeners}
                    {...attributes}
                    style={{
                        fontSize: 16,
                        color: '#999',
                        cursor: 'grab',
                    }}
                />
                <Typography.Text
                    style={{
                        marginLeft: 8,
                        flex: 1,
                        color: column.hidden ? '#999' : '#000',
                    }}
                >
                    {typeof column.title === 'function' ? column.title() : column.title}
                </Typography.Text>
                <ToggleVisibilityButton column={column} onToggleVisibility={onToggleVisibility} />
            </List.Item>
        </div>
    );
};

const ToggleVisibilityButton = ({
    column,
    onToggleVisibility,
}: {
    column: { key: React.Key; hidden?: boolean; hideable?: boolean };
    onToggleVisibility: () => void;
}) => {
    const hideable = column.hideable !== false;
    const button = (
        <Button
            type="text"
            disabled={!hideable}
            icon={<VisibilityIcon hidden={!!column.hidden} hideable={hideable} />}
            onClick={onToggleVisibility}
            style={{
                padding: 0,
                fontSize: 16,
                color: !hideable ? '#ccc' : column.hidden ? '#999' : PRIMARY_COLOR,
                cursor: !hideable ? 'not-allowed' : undefined,
            }}
        />
    );

    return hideable ? button : <Tooltip title="This column cannot be hidden">{button}</Tooltip>;
};

const VisibilityIcon = ({ hidden, hideable }: { hidden: boolean; hideable: boolean }) => {
    const color = hideable ? (hidden ? '#999' : 'var(--ant-color-primary)') : '#ccc';

    return hidden ? <EyeInvisibleOutlined style={{ fontSize: 16, color }} /> : <EyeOutlined style={{ fontSize: 16, color }} />;
};
